class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce(/** @param {Event} event */ (event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = /** @type {HTMLFormElement|null} */ (this.querySelector('form'));
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {facetWrapper.addEventListener('keyup', onKeyUpEscape);}
  }

  static setListeners() {
    /** @param {PopStateEvent} event */
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) {return;}
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * @param {string} searchParams
   * @param {Event|null} event
   * @param {boolean} [updateURLHash]
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) {
        collection.classList.add('loading');
      }
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      /** @param {{url: string, html: string}} element */
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {FacetFiltersForm.updateURLHash(searchParams);}
  }

  /**
   * @param {string} url
   * @param {Event|null} event
   */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {initializeScrollAnimationTrigger(html);}
      });
  }

  /**
   * @param {function({url: string, html: string}): boolean} filterDataUrl
   * @param {Event|null} event
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const cachedData = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cachedData) {return;}
    const html = cachedData.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {initializeScrollAnimationTrigger(html);}
  }

  /**
   * @param {string} html
   */
  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const parsedContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer');
    if (!container || !parsedContainer) {return;}
    container.innerHTML = parsedContainer.innerHTML;

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  /**
   * @param {string} html
   */
  static renderProductCount(html) {
    const parsedProductCount = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    if (!parsedProductCount) {return;}
    const count = parsedProductCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  /**
   * @param {string} html
   * @param {Event|null} event
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    /**
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    const matchesIndex = (element) => {
      if (!event || !event.target) {return false;}
      const jsFilter = /** @type {HTMLElement|null} */ (/** @type {HTMLElement} */ (event.target).closest('.js-filter'));
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = /** @type {HTMLElement[]} */ (Array.from(facetDetailsElements)).filter((element) => !matchesIndex(element));
    const countsToRender = /** @type {HTMLElement|undefined} */ (/** @type {HTMLElement[]} */ (Array.from(facetDetailsElements)).find(matchesIndex));

    facetsToRender.forEach((element) => {
      const targetFilter = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (targetFilter) {
        targetFilter.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target) {
      const targetJsFilter = /** @type {HTMLElement|null} */ (/** @type {HTMLElement} */ (event.target).closest('.js-filter'));
      if (targetJsFilter) {
        FacetFiltersForm.renderCounts(countsToRender, targetJsFilter);
      }
    }
  }

  /**
   * @param {Document} html
   */
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) {return;}
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * @param {Document} html
   */
  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      if (!sourceElement) {return;}
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = sourceElement.innerHTML;
      }
    });

    const facetFiltersFormMobile = document.getElementById('FacetFiltersFormMobile');
    if (facetFiltersFormMobile) {
      const menuDrawer = /** @type {(HTMLElement & {bindEvents: () => void})|null} */ (facetFiltersFormMobile.closest('menu-drawer'));
      if (menuDrawer) {
        menuDrawer.bindEvents();
      }
    }
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   */
  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      /** @type {HTMLElement} */ (target.querySelector('.facets__selected')).outerHTML = /** @type {HTMLElement} */ (source.querySelector('.facets__selected')).outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      /** @type {HTMLElement} */ (target.querySelector('.facets__summary')).outerHTML = /** @type {HTMLElement} */ (source.querySelector('.facets__summary')).outerHTML;
    }
  }

  /**
   * @param {string} searchParams
   */
  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * @returns {{section: string|undefined}[]}
   */
  static getSections() {
    const productGrid = document.getElementById('product-grid');
    return [
      {
        section: productGrid ? productGrid.dataset.id : undefined,
      },
    ];
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(/** @type {any} */ (formData)).toString();
  }

  /**
   * @param {string} searchParams
   * @param {Event} event
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * @param {Event} event
   */
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const targetElement = /** @type {HTMLElement|null} */ (event.target);
    if (!targetElement) {return;}

    if (targetElement.className === 'mobile-facets__checkbox') {
      const closestForm = /** @type {HTMLFormElement|null} */ (targetElement.closest('form'));
      if (closestForm) {
        const searchParams = this.createSearchParams(closestForm);
        this.onSubmitForm(searchParams, event);
      }
    } else {
      /** @type {string[]} */
      const forms = [];
      const closestForm = /** @type {HTMLFormElement|null} */ (targetElement.closest('form'));
      const isMobile = closestForm ? closestForm.id === 'FacetFiltersFormMobile' : false;

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(/** @type {HTMLFormElement} */ (form)));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(/** @type {HTMLFormElement} */ (form)));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  /**
   * @param {Event} event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const currentTarget = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    const url =
      currentTarget.href.indexOf('?') === -1
        ? ''
        : currentTarget.href.slice(currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, null);
  }
}

/** @type {{url: string, html: string}[]} */
FacetFiltersForm.filterData = [];
/** @type {string} */
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
/** @type {string} */
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  /**
   * @param {Event} event
   */
  onRangeChange(event) {
    this.adjustToValidValues(/** @type {HTMLInputElement} */ (event.currentTarget));
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = /** @type {HTMLInputElement|undefined} */ (inputs[0]);
    const maxInput = /** @type {HTMLInputElement|undefined} */ (inputs[1]);
    if (!minInput || !maxInput) {return;}
    if (maxInput.value) {minInput.setAttribute('max', maxInput.value);}
    if (minInput.value) {maxInput.setAttribute('min', minInput.value);}
    if (minInput.value === '') {maxInput.setAttribute('min', '0');}
    if (maxInput.value === '') {
      const maxAttr = maxInput.getAttribute('max');
      if (maxAttr) {minInput.setAttribute('max', maxAttr);}
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) {input.value = String(min);}
    if (value > max) {input.value = String(max);}
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = /** @type {HTMLAnchorElement|null} */ (this.querySelector('a'));
    if (!facetLink) {return;}
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') {this.closeFilter(event);}
    });
  }

  /**
   * @param {Event} event
   */
  closeFilter(event) {
    event.preventDefault();
    const form = /** @type {FacetFiltersForm|null} */ (this.closest('facet-filters-form') || document.querySelector('facet-filters-form'));
    if (form) {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define('facet-remove', FacetRemove);
